'use client';

import { useState } from 'react';
import { CURRENT_PHOTO_CONSENT_VERSION, type PhotoConsentInput } from '@/lib/race-photos/consent';
import { useIsMobile } from '@/hooks/use-mobile';
import { StepHeading, StepText, stepInputStyle, stepPrimaryStyle, type PhotoVariant } from './photo-flow-ui';

interface ThemeColors {
  primary: string;
  secondary: string;
  text: string;
  textMuted: string;
  buttonText?: string;
}

interface PhotoConsentGateProps {
  /** Kept for callers; the colours come from the v8 tokens around the gate. */
  themeColors?: ThemeColors;
  onConsent: (payload: PhotoConsentInput) => void;
  /** 'hub' for the athlete hub's look; the consent itself is identical. */
  variant?: PhotoVariant;
}

type AgeBand = 'over_18' | 'under_18';

/**
 * Consent gate shown before the camera opens. Facial recognition is special-
 * category biometric data, so we take (and record) explicit consent first. An
 * adult consents for themselves; someone under 18 needs a parent or guardian to
 * consent and give their name. The camera never starts until this is satisfied.
 */
export function PhotoConsentGate({ onConsent, variant = 'page' }: PhotoConsentGateProps) {
  const isMobile = useIsMobile();
  const [ageBand, setAgeBand] = useState<AgeBand | null>(null);
  const [selfConsent, setSelfConsent] = useState(false);
  const [guardianName, setGuardianName] = useState('');
  const [guardianConsent, setGuardianConsent] = useState(false);

  const valid =
    ageBand === 'over_18'
      ? selfConsent
      : ageBand === 'under_18'
        ? guardianConsent && guardianName.trim().length >= 2
        : false;

  const handleContinue = () => {
    if (!valid || !ageBand) return;
    onConsent({
      ageBand,
      selfConsent: ageBand === 'over_18' ? true : undefined,
      guardianName: ageBand === 'under_18' ? guardianName.trim() : undefined,
      guardianConsent: ageBand === 'under_18' ? true : undefined,
      consentVersion: CURRENT_PHOTO_CONSENT_VERSION,
    });
  };

  const ageButton = (band: AgeBand, label: string) => {
    const active = ageBand === band;
    return (
      <button
        type="button"
        aria-pressed={active}
        onClick={() => setAgeBand(band)}
        className={active ? undefined : 'fvd-hoverable'}
        style={{
          flex: 1,
          minHeight: 44,
          padding: '0 12px',
          borderRadius: 'var(--fv-radius-control, 8px)',
          // Chosen: an ink outline, as every v8 option. Teal stays on the action.
          border: `1px solid ${active ? 'var(--fvd-ink)' : 'var(--fvd-border-control)'}`,
          background: 'transparent',
          color: active ? 'var(--fvd-ink)' : 'var(--fvd-ink-secondary)',
          fontFamily: 'inherit',
          fontSize: 13.5,
          fontWeight: active ? 600 : 500,
          cursor: 'pointer',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </button>
    );
  };

  const checkboxRow = (
    checked: boolean,
    onChange: (v: boolean) => void,
    label: string,
  ) => (
    <label
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '10px',
        textAlign: 'left',
        cursor: 'pointer',
        fontSize: 13,
        lineHeight: 1.5,
        color: 'var(--fvd-ink)',
        // The whole row is the tap target on a phone.
        minHeight: 44,
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ width: '20px', height: '20px', marginTop: '1px', flex: '0 0 auto', accentColor: 'var(--fvd-teal-solid)' }}
      />
      <span>{label}</span>
    </label>
  );

  return (
    <div style={{ textAlign: 'left', maxWidth: 520 }}>
      <StepHeading variant={variant}>Before we find your photos</StepHeading>
      <StepText variant={variant} style={{ marginBottom: 16 }}>
        We use facial recognition to match your face with the event photos. Your selfie is
        deleted within 24 hours and your face data once the event photos are no longer available
        (and within 12 months at the latest).
      </StepText>

      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
        {ageButton('over_18', "I'm 18 or over")}
        {ageButton('under_18', "I'm under 18")}
      </div>

      {ageBand === 'over_18' && (
        <div style={{ marginBottom: '20px' }}>
          {checkboxRow(
            selfConsent,
            setSelfConsent,
            'I consent to facial recognition being used to find my photos from this event.',
          )}
        </div>
      )}

      {ageBand === 'under_18' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '20px' }}>
          <p style={{ fontSize: '13px', color: 'var(--fvd-ink-secondary)', margin: 0, textAlign: 'left' }}>
            A parent or guardian needs to give permission and complete this for you.
          </p>
          <input
            type="text"
            value={guardianName}
            onChange={(e) => setGuardianName(e.target.value)}
            placeholder="Parent or guardian full name"
            aria-label="Parent or guardian full name"
            style={stepInputStyle(variant, isMobile)}
          />
          {checkboxRow(
            guardianConsent,
            setGuardianConsent,
            'I am this person’s parent or guardian and I consent to facial recognition being used to find their photos from this event.',
          )}
        </div>
      )}

      <button
        type="button"
        onClick={handleContinue}
        disabled={!valid}
        className={valid ? 'fvd-hoverable-solid' : undefined}
        style={stepPrimaryStyle(variant, { isMobile, disabled: !valid })}
      >
        Continue to camera
      </button>
    </div>
  );
}
